import { useEffect, useState } from 'react';
import type { Unsubscribe } from 'firebase/firestore';

export type Starter<T> = (onData: (value: T) => void, onError: (err: Error) => void) => Unsubscribe;

export interface Watched<T> {
  data: T;
  loading: boolean;
  error: string | null;
}

/**
 * Follows one live Firestore query. Pass a `useCallback`-stable `start`, or `null` to wait (for
 * example until a branch is chosen). Errors are kept, not thrown: a screen shows them.
 */
export function useWatch<T>(start: Starter<T> | null, empty: T): Watched<T> {
  const [state, setState] = useState<Watched<T>>({
    data: empty,
    loading: start !== null,
    error: null,
  });
  useEffect(() => {
    if (!start) return;
    return start(
      (data) => setState({ data, loading: false, error: null }),
      (err) => setState((s) => ({ ...s, loading: false, error: err.message })),
    );
  }, [start]);
  return state;
}
